import { isKnown } from '../../store/assessmentFacts.js';
import { money, ROMAN } from './format.js';

// Moment two: the goal sheet advisors already know, in the app.
//
// The two-part outline and the `+Tax+COLA` shorthand are kept because they are
// what the paper version has always said and what the advisor is trained to
// talk through. What is new is the honesty: a figure the household never gave
// us is not printed, a required-at-retirement number is not invented from the
// current year, and the difference at the foot only appears when both sides of
// it are recorded.

const BASIS =
  'Recorded amounts are annual and as entered. No taxes, inflation, investment returns '
  + 'or unrecorded withdrawals are modelled here.';

function Row({ n, label, value, qualifier, className }) {
  return (
    <div className={`conv-gs-row${className ? ` ${className}` : ''}`}>
      <span className="conv-gs-num">{n}</span>
      <span>{label}</span>
      <span className="conv-gs-value">
        {value}
        {qualifier ? <small>{qualifier}</small> : null}
      </span>
    </div>
  );
}

export default function GoalSheetView({ facts, comparison }) {
  const { pools, unclassified } = facts;
  const everyPoolKnown = Object.values(pools).every(isKnown);
  const unclassifiedOpen = isKnown(unclassified) && unclassified.value > 0;
  const poolsTotal =
    everyPoolKnown && !unclassifiedOpen
      ? pools.taxDeferred.value + pools.roth.value + pools.taxable.value
      : null;

  const retired = isKnown(facts.status) && facts.status.raw === 'Retired';
  const spendConflict = facts.spending.state === 'conflict';

  const differenceLabel = !comparison.ready
    ? 'Difference to establish'
    : comparison.direction === 'short'
      ? 'Shortfall to address'
      : comparison.direction === 'above'
        ? 'Income above recorded spending'
        : 'Income and spending balance';

  return (
    <>
      <div className="conv-eyebrow">Goals{facts.name ? ` · ${facts.name}` : ''}</div>
      <h1 className="conv-goal-title">Financial planning goals and concerns</h1>

      <div className="conv-gs-period">
        <span className="conv-tag">{facts.period}</span>
        <span className="conv-quiet">Recorded amounts &middot; annual income and spending</span>
      </div>

      <div className="conv-gs-sections">
        <section className="conv-gs-section">
          <div className="conv-gs-heading">
            <span>01</span>
            <h2>Preservation and conservative growth of principal</h2>
          </div>
          <div className="conv-gs-group">
            <h3>Investment accounts</h3>
            <Row n="i." label="Tax-deferred investments" value={money(pools.taxDeferred)} />
            <Row n="ii." label="Roth investments" value={money(pools.roth)} />
            <Row n="iii." label="Taxable investments" value={money(pools.taxable)} />
            {unclassifiedOpen && (
              <Row n="iv." label="Account type to confirm" value={money(unclassified)} />
            )}
            <Row
              n=""
              label="Total classified investments"
              value={poolsTotal === null ? 'Not available' : money(poolsTotal)}
              className="conv-gs-total"
            />

          </div>
        </section>

        <section className="conv-gs-section">
          <div className="conv-gs-heading">
            <span>02</span>
            <h2>Retirement income and cash flow strategy</h2>
          </div>
          <div className="conv-gs-group">
            <Row
              n="a."
              label="Income required today"
              value={spendConflict ? 'Needs clarification' : money(facts.spending)}
              qualifier={isKnown(facts.spending) && !spendConflict ? '+Tax+COLA' : ''}
            />
            {!retired && (
              <Row n="b." label="Income required at retirement" value="Not yet established" />
            )}

            <h3>Income sources for this period</h3>
            {facts.income.rows.length ? (
              facts.income.rows.map((r, i) => (
                <Row key={`${r.key}-${i}`} n={ROMAN[i] || ''} label={r.label} value={money(r.amount)} />
              ))
            ) : (
              <Row n="" label="Income sources" value="Not recorded" />
            )}
            <Row
              n=""
              label={facts.income.complete ? 'Total recorded income' : 'Known income subtotal'}
              value={money(facts.income)}
              className="conv-gs-total"
            />
            <Row
              n=""
              label={differenceLabel}
              value={comparison.ready ? money(Math.abs(comparison.gap)) : 'Not available'}
              qualifier={comparison.ready && comparison.direction === 'short' ? '+Tax+COLA' : ''}
              className={comparison.ready && comparison.direction === 'short' ? 'conv-gs-short' : 'conv-gs-result'}
            />

          </div>
        </section>
      </div>

      <p className="conv-caption conv-gs-foot">
        +Tax+COLA refers to the taxes and future cost-of-living adjustments that
        still need to be considered; neither is calculated here. {BASIS}
      </p>
    </>
  );
}
